/**
 * CompanyAccessTab — Team Members → Company Access Tab (INT-10)
 *
 * Displayed inside TeamMemberDetailsDialog as a "Company Access" tab.
 * Shows all companies in the organisation with toggles for access and default.
 *
 * Confirmation dialog fires when a user toggles a company OFF.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Building2,
  Loader2,
  Save,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import * as integrationsService from '@/services/integrationsRedesignService';
import type { EmployeeCompanyAccessRow } from '@/types';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface CompanyAccessTabProps {
  /** The employee ID whose access we're managing */
  employeeId: string;
  /** The employee's display name (used in confirmation dialogs) */
  employeeName: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CompanyAccessTab({ employeeId, employeeName }: CompanyAccessTabProps) {
  const [rows, setRows] = useState<EmployeeCompanyAccessRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // The company being toggled OFF — triggers confirmation dialog
  const [pendingRemoval, setPendingRemoval] = useState<EmployeeCompanyAccessRow | null>(null);

  const loadAccess = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await integrationsService.getEmployeeCompanyAccess(employeeId);
      setRows(data);
      setIsDirty(false);
    } catch {
      toast.error('Failed to load company access');
    } finally {
      setIsLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    loadAccess();
  }, [loadAccess]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  function handleToggle(companyId: string, checked: boolean) {
    if (!checked) {
      // Show confirmation before removing access
      const row = rows.find((r) => r.companyId === companyId);
      if (row) setPendingRemoval(row);
      return;
    }
    // Granting access — update immediately (no confirmation needed)
    setRows((prev) =>
      prev.map((r) =>
        r.companyId === companyId ? { ...r, hasAccess: true } : r
      )
    );
    setIsDirty(true);
  }

  function confirmRemoveAccess() {
    if (!pendingRemoval) return;
    const removing = pendingRemoval;
    setPendingRemoval(null);

    setRows((prev) =>
      prev.map((r) => {
        if (r.companyId === removing.companyId) {
          return { ...r, hasAccess: false, isDefault: false };
        }
        return r;
      })
    );
    setIsDirty(true);
  }

  function handleSetDefault(companyId: string) {
    setRows((prev) =>
      prev.map((r) => ({
        ...r,
        isDefault: r.companyId === companyId && r.hasAccess,
      }))
    );
    setIsDirty(true);
  }

  async function handleSave() {
    setIsSaving(true);
    try {
      const updates = rows.map((r) => ({
        companyId: r.companyId,
        hasAccess: r.hasAccess,
        isDefault: r.isDefault,
      }));
      await integrationsService.updateEmployeeCompanyAccess(employeeId, updates);
      setIsDirty(false);
      toast.success('Company access saved', {
        description: `${employeeName}'s company access has been updated.`,
      });
    } catch {
      toast.error('Failed to save company access');
    } finally {
      setIsSaving(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Render — loading
  // ---------------------------------------------------------------------------

  if (isLoading) {
    return (
      <div className="space-y-3 py-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex items-center justify-between py-2">
            <div className="flex items-center gap-3">
              <Skeleton className="h-8 w-8 rounded-md" />
              <div className="space-y-1">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-12" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Skeleton className="h-4 w-20 rounded" />
              <Skeleton className="h-5 w-9 rounded-full" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const hasAccess = rows.filter((r) => r.hasAccess);
  const hasDefault = rows.some((r) => r.isDefault);

  return (
    <div className="space-y-4">
      {/* Company rows */}
      <div className="space-y-1">
        {rows.map((row) => (
          <div
            key={row.companyId}
            className={cn(
              'flex items-center justify-between py-2.5 px-3 rounded-lg transition-colors',
              row.hasAccess ? 'hover:bg-muted/50' : 'opacity-60 hover:bg-muted/30'
            )}
          >
            {/* Left: company info */}
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex h-8 w-8 items-center justify-center rounded-md bg-muted flex-shrink-0">
                <Building2 className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium truncate">{row.companyName}</span>
                  {row.isDefault && (
                    <Badge variant="secondary" className="text-xs py-0 h-4">
                      Default
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {row.companyCode}
                  {row.companyStatus === 'inactive' && (
                    <span className="ml-1.5 text-muted-foreground/60">(inactive)</span>
                  )}
                </div>
              </div>
            </div>

            {/* Right: set-default + toggle */}
            <div className="flex items-center gap-3 flex-shrink-0">
              {/* Set as Default button — only shows when this company has access but isn't default */}
              {row.hasAccess && !row.isDefault && (
                <button
                  className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
                  onClick={() => handleSetDefault(row.companyId)}
                >
                  Set as default
                </button>
              )}

              {/* Access toggle */}
              <div className="flex items-center gap-1.5">
                <Label htmlFor={`access-${row.companyId}`} className="sr-only">
                  {row.hasAccess ? 'Has access' : 'No access'}
                </Label>
                <Switch
                  id={`access-${row.companyId}`}
                  checked={row.hasAccess}
                  onCheckedChange={(checked) => handleToggle(row.companyId, checked)}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Summary */}
      {!isLoading && (
        <p className="text-xs text-muted-foreground px-3">
          {hasAccess.length === 0
            ? 'No companies assigned.'
            : `${hasAccess.length} compan${hasAccess.length === 1 ? 'y' : 'ies'} assigned${
                hasDefault ? '' : ' — no default set'
              }.`}
        </p>
      )}

      {/* Save button */}
      {isDirty && (
        <div className="pt-2 px-3">
          <Button
            size="sm"
            onClick={handleSave}
            disabled={isSaving}
            className="w-full gap-2"
          >
            {isSaving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Save className="h-3.5 w-3.5" />
            )}
            Save Changes
          </Button>
        </div>
      )}

      {/* Confirmation dialog — removing access */}
      <AlertDialog
        open={!!pendingRemoval}
        onOpenChange={(o) => { if (!o) setPendingRemoval(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove access?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove{' '}
              <strong>{pendingRemoval?.companyName}</strong> from{' '}
              <strong>{employeeName}</strong>'s company switcher.{' '}
              {pendingRemoval?.isDefault
                ? 'Since this is their default company, their default will be reset.'
                : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRemoveAccess}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove Access
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
